import { useState } from "react";
import useSWRMutation from "swr/mutation";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Section } from "@/components/section";
import { UserSelector } from "@/components/user-selector";
import type { PickerUser } from "@/components/user-selector";
import { UserChip } from "@/components/user-chip";
import { useUser } from "@/hooks/users";
import { putFetcher } from "@/lib/swr";
import { useToast } from "@/hooks/use-toast";
import { toastError } from "@/lib/toast";
import { ROLLOUT_TASK_LABELS } from "@/lib/rollout-task";
import type { App, RolloutDataConfig, RolloutDataType } from "@/types/domain";
import { refreshApps } from "./refresh-apps";

const TYPES: RolloutDataType[] = ["commercial", "data_check"];

const normalize = (c: RolloutDataConfig | null | undefined): RolloutDataConfig => ({
  commercial: c?.commercial ?? { enabled: false, assigneeUserId: null },
  data_check: c?.data_check ?? { enabled: false, assigneeUserId: null },
});

const same = (a: RolloutDataConfig, b: RolloutDataConfig) =>
  TYPES.every(
    (t) =>
      !!a[t]?.enabled === !!b[t]?.enabled &&
      (a[t]?.assigneeUserId ?? null) === (b[t]?.assigneeUserId ?? null)
  );

const Row = ({
  type,
  entry,
  onChange,
}: {
  type: RolloutDataType;
  entry: { enabled: boolean; assigneeUserId: string | null };
  onChange: (next: { enabled: boolean; assigneeUserId: string | null }) => void;
}) => {
  const assignee = useUser(entry.assigneeUserId);
  return (
    <div className="flex flex-wrap items-center gap-3 text-sm">
      <label className="flex w-28 items-center gap-2">
        <Switch
          checked={entry.enabled}
          onCheckedChange={(v) => onChange({ ...entry, enabled: v === true })}
          aria-label={`需要${ROLLOUT_TASK_LABELS[type]}`}
        />
        <span className="font-medium">{ROLLOUT_TASK_LABELS[type]}</span>
      </label>
      <div className="flex min-w-0 items-center gap-2">
        {assignee ? (
          <UserChip user={assignee} />
        ) : (
          <span className="text-xs text-muted-foreground">
            {entry.enabled ? "未指定负责人" : "—"}
          </span>
        )}
        <UserSelector
          selectedUser={(assignee as PickerUser | undefined) ?? null}
          onContinue={(u: PickerUser | null) =>
            onChange({ ...entry, assigneeUserId: u ? String(u.id) : null })
          }
          buttonText={assignee ? "Change" : "Assign"}
          title={`${ROLLOUT_TASK_LABELS[type]}负责人`}
          description="该 App 这份数据由谁回收并提交报告。"
        />
      </div>
    </div>
  );
};

// 放量数据回收(PRD d4eefc2e):每个 App 分别配「需要收入数据 / 版本数据」与
// 负责人。渠道发布走到 rolling 带百分比时(无渠道的 App:版本进入放量/上线时)
// 按此生成任务;确认须挂报告。规则在服务端,这里只是配置入口。
export const RolloutDataSection = ({ app }: { app: App }) => {
  const { toast } = useToast();
  const saved = normalize(app.rolloutData);
  const [config, setConfig] = useState<RolloutDataConfig>(saved);
  const dirty = !same(config, saved);
  const invalid = TYPES.some(
    (t) => config[t]?.enabled && !config[t]?.assigneeUserId
  );

  const { trigger, isMutating } = useSWRMutation(
    `/apps/${app.id}/rollout-data`,
    putFetcher,
    {
      onSuccess: () => {
        refreshApps();
        toast({ title: "放量数据回收配置已保存" });
      },
      onError: (err: unknown) => toastError(toast, "保存失败", err),
    }
  );

  return (
    <Section
      title="放量数据回收"
      action={
        <Button
          size="sm"
          className="h-8"
          disabled={!dirty || invalid || isMutating}
          onClick={() => trigger({ config })}
        >
          {isMutating ? "保存中…" : "保存"}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        {TYPES.map((t) => (
          <Row
            key={t}
            type={t}
            entry={config[t] ?? { enabled: false, assigneeUserId: null }}
            onChange={(next) => setConfig((c) => ({ ...c, [t]: next }))}
          />
        ))}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        收入数据:每个渠道放量阶段各一份,提交后通知放量操作人可继续放量;版本数据:
        只在放量 100% / 上线时一份(放量期会被比例带偏)。没有渠道的 App 在版本进入放量 /
        上线时生成。提交报告即完成;开启后必须指定负责人。
      </p>
    </Section>
  );
};

export default RolloutDataSection;
